import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import { CircularProgress, Paper, Box } from "@mui/material";
import styles from "./Dashboard.module.scss";
import { LoadingButton } from "@mui/lab";
import QuinipolosToAnswer from "../../Components/QuinipolosToAnswer/QuinipolosToAnswer";
import {
  UserDataType,
  useUser as useUserData,
} from "../../Context/UserContext/UserContext";
import { getLeagueIcon } from "../../utils/leagueIcons";
import LeagueIconBadge from "../../Components/LeagueIconBadge/LeagueIconBadge";
import { useTranslation } from "react-i18next";
import { apiGet } from "../../utils/apiUtils";
import { isSystemAdmin } from "../../utils/moderatorUtils";
import { config } from "../../utils/config";
import { LeagueStatusChip } from "../../Components/LeagueStatusChip/LeagueStatusChip";
import {
  compareLeaguesActiveFirst,
  isLeagueFinished,
} from "../../utils/leagueStatus";

const Dashboard = () => {
  const navigate = useNavigate();
  const { userData, updateUser } = useUserData();
  const [leagues, setLeagues] = useState<
    {
      leagueId: string;
      leagueName: string;
      participants: string[];
      icon_style?: {
        icon?: string;
        accent_color?: string;
        icon_color?: string;
      };
      status?: string;
    }[]
  >([]);
  const { t } = useTranslation();
  const [hasFetchedProfile, setHasFetchedProfile] = useState(false);

  const renderLeagueIcon = (
    icon?: string,
    accentColor?: string,
    iconColor?: string
  ) => {
    return (
      <LeagueIconBadge
        icon={icon}
        accentColor={accentColor}
        iconColor={iconColor}
      />
    );
  };

  useEffect(() => {
    if (
      !hasFetchedProfile &&
      userData.isAuthenticated &&
      (!userData.leagues || userData.leagues.length === 0)
    ) {
      apiGet<UserDataType>("/api/users/me/profile")
        .then((profile) => {
          const leagues = profile.leagues || [];
          leagues.sort((a, b) =>
            a.leagueId === "global" ? -1 : b.leagueId === "global" ? 1 : 0
          );
          updateUser({
            leagues,
            role: profile.role,
            username: profile.username,
          });
          setHasFetchedProfile(true);
        })
        .catch((error) => {
          console.error("Error fetching user profile:", error);
          // If it's an authentication error, redirect to login
          if (error.response?.status === 401) {
            window.location.href = "/sign-in";
          }
        });
    }
  }, [
    userData.leagues,
    userData.isAuthenticated,
    updateUser,
    hasFetchedProfile,
  ]);

  const getLeaguesData = useCallback(async () => {
    const leaguesData = await Promise.all(
      userData.leagues.map((league) => {
        return apiGet<any>(`/api/leagues/${league}`).then((leagueData) => {
          return leagueData;
        });
      })
    );

    const leaguesWithData = leaguesData.map((league) => {
      return {
        leagueId: league.id,
        leagueName: league.league_name,
        participants: league.participants,
        icon_style: league.icon_style,
        status: league.status,
      };
    });
    setLeagues(leaguesWithData.slice().sort(compareLeaguesActiveFirst));
  }, [userData.leagues]);

  useEffect(() => {
    if (userData.leagues.length > 0) {
      getLeaguesData();
    }
  }, [getLeaguesData, userData.leagues.length]);

  return (
    <div className={styles.dashboardContainer}>
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          p: window.innerWidth > 400 ? 4 : 2,
          borderRadius: "20px",
        }}
      >
        <div className={styles.container}>
          <h2
            className={styles.leaguesTitle}
            style={{ marginTop: 0, borderBottom: "1px solid #e0e0e0" }}
          >
            {t("quinipolos")}
          </h2>
          <QuinipolosToAnswer appLocation="user-dashboard" />
          <div className={styles.leaguesContainer}>
            <h2 className={styles.leaguesTitle}>{t("myLeagues")}</h2>
            {userData.role === "" ? (
              <CircularProgress sx={{ mt: 4 }} />
            ) : userData.leagues.length === 0 ? (
              <p className={styles.noActionsMessage}>{t("noLeagues")}</p>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {leagues.map((league) => (
                  <Button
                    className="gradient-primary"
                    sx={{
                      display: "flex",
                      width: "100%",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      padding: "10px",
                      minHeight: "60px",
                    }}
                    key={league.leagueId}
                    onClick={() => {
                      navigate(`/league-dashboard?id=${league.leagueId}`);
                    }}
                    variant={"contained"}
                  >
                    <>
                      {renderLeagueIcon(
                        league.icon_style?.icon,
                        league.icon_style?.accent_color,
                        league.icon_style?.icon_color
                      )}
                      <p
                        style={{
                          marginLeft: "20px",
                        }}
                      >
                        <b>{league.leagueName}</b>
                      </p>
                      {isLeagueFinished(league) && (
                        <LeagueStatusChip
                          status={league.status}
                          contrast="inherit"
                          sx={{ ml: "auto" }}
                        />
                      )}
                    </>
                  </Button>
                ))}
              </Box>
            )}
            <h2 className={styles.leaguesTitle}>{t("actions")}</h2>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {config.isDevelopment && (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate("/graphics")}
                    sx={{ width: "100%" }}
                  >
                    {t("graphicsGenerator")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() =>
                      navigate("/quinipolo/correct?id=mock-correction&correct")
                    }
                    sx={{ width: "100%" }}
                  >
                    Correct fake quinipolo (dev)
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate("/teams-curator")}
                    sx={{ width: "100%" }}
                  >
                    Teams Curator
                  </Button>
                </>
              )}
              {/* Admin Link - Only visible to system admins */}
              {isSystemAdmin(userData.role) ? (
                <LoadingButton
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate("/admin")}
                >
                  {t("goToAdminPanel")}
                </LoadingButton>
              ) : null}

              <Button
                className="gradient-secondary"
                variant="contained"
                color="primary"
                size="large"
                onClick={() => {
                  navigate("/join-league");
                }}
                style={{ width: "100%" }}
              >
                {t("viewAllLeagues")}
              </Button>

              <LoadingButton
                className="gradient-secondary"
                loading={userData.role === ""}
                variant="contained"
                color="primary"
                onClick={() => {
                  navigate("/create-league");
                }}
                size="large"
                style={{
                  marginRight: "20px",
                  width: "100%",
                }}
              >
                {t("createLeague")}
              </LoadingButton>
            </Box>
          </div>
        </div>
      </Paper>
    </div>
  );
};

export default Dashboard;
